import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/cn";
import { useAppSelector } from "@/hooks/redux";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { submitAiFeedback } from "@/services/aiFeedbackService";

interface FeedbackButtonsProps {
  messageId: string;
}

// Barre d'évaluation sous chaque réponse terminée de l'assistant.
// 👍 envoie immédiatement ; 👎 ouvre un champ commentaire (optionnel) avant
// l'envoi. Re-cliquer change l'avis : le backend upsert sur
// (userId, sessionId, messageId), rien ne s'empile. Le transcript complet de
// la conversation est archivé avec le feedback — c'est lui que l'admin
// consultera, et il nourrira le futur apprentissage.
export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
}) => {
  const { t } = useTranslation();
  const sessionId = useAppSelector((s) => s.aiChat.sessionId);
  const messages = useAppSelector((s) => s.aiChat.messages);
  const generatedCode = useAppSelector((s) => s.aiChat.generatedCode);

  const [submitted, setSubmitted] = useState<"up" | "down" | null>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (rating: "up" | "down", withComment: string | null) => {
    if (sending) return;
    setSending(true);
    try {
      await submitAiFeedback({
        sessionId,
        messageId,
        rating,
        comment: withComment,
        conversation: messages,
        generatedCode,
      });
      setSubmitted(rating);
      setCommentOpen(false);
      toast.success(t("aiAgent.feedback.thanks"));
    } catch {
      toast.error(t("aiAgent.feedback.error"));
    } finally {
      setSending(false);
    }
  };

  const handleThumbUp = () => {
    setCommentOpen(false);
    void send("up", null);
  };

  const handleThumbDown = () => {
    // Le commentaire est la matière première du feedback négatif : on ouvre
    // le champ au lieu d'envoyer directement (envoi possible sans texte).
    setCommentOpen(true);
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={handleThumbUp}
          disabled={sending}
          className={cn(
            "p-1.5 rounded hover:bg-muted/80 transition-colors cursor-pointer disabled:opacity-50",
            submitted === "up" && "bg-emerald-100 dark:bg-emerald-900/40",
          )}
          aria-label={t("aiAgent.feedback.good")}
          title={t("aiAgent.feedback.good")}
        >
          <ThumbsUp
            className={cn(
              "h-3.5 w-3.5",
              submitted === "up"
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-muted-foreground",
            )}
          />
        </button>
        <button
          type="button"
          onClick={handleThumbDown}
          disabled={sending}
          className={cn(
            "p-1.5 rounded hover:bg-muted/80 transition-colors cursor-pointer disabled:opacity-50",
            submitted === "down" && "bg-red-100 dark:bg-red-900/40",
          )}
          aria-label={t("aiAgent.feedback.bad")}
          title={t("aiAgent.feedback.bad")}
        >
          <ThumbsDown
            className={cn(
              "h-3.5 w-3.5",
              submitted === "down"
                ? "text-red-600 dark:text-red-400"
                : "text-muted-foreground",
            )}
          />
        </button>
        {sending && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
        {submitted && !sending && (
          <span className="text-[11px] text-muted-foreground ml-1">
            {t("aiAgent.feedback.recorded")}
          </span>
        )}
      </div>

      {commentOpen && (
        <div className="w-full space-y-2">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 2000))}
            rows={3}
            placeholder={t("aiAgent.feedback.commentPlaceholder")}
            className="text-sm"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="h-7 cursor-pointer"
              disabled={sending}
              onClick={() => void send("down", comment.trim() || null)}
            >
              {t("aiAgent.feedback.send")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 cursor-pointer"
              disabled={sending}
              onClick={() => setCommentOpen(false)}
            >
              {t("aiAgent.feedback.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
